import KitButton from '@omni/kit/components/KitButton';
import KitContainedIcon from '@omni/kit/components/KitContainedIcon';
import KitText from '@omni/kit/components/KitText';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import React, { ReactElement } from 'react';
import { StyleSheet, View } from 'react-native';

const TooManyAttemptsScreen = ({
  reset,
  retryAfter,
  isEmailVerification,
}: {
  reset: () => void;
  retryAfter: string | null;
  isEmailVerification?: boolean;
}): ReactElement => {
  // ------------------------------------------------------------
  // Context & Custom Hooks

  const { t } = useTranslation(['auth', 'common']);

  // ------------------------------------------------------------
  // Computed Values

  const retryAfterMinutes = retryAfter
    ? Math.ceil(parseInt(retryAfter, 10) / 60)
    : null;

  const getDuration = () => {
    if (!retryAfterMinutes || retryAfterMinutes === 60) {
      return `1 ${t('common:hour').toLowerCase()}`;
    }

    if (retryAfterMinutes === 1) {
      return t('common:numMinutes_one', { count: 1 });
    }

    return t('common:numMinutes_other', { count: retryAfterMinutes });
  };
  const duration = getDuration();

  return (
    <View style={[styles.contentContainer]}>
      <View style={styles.iconContainer}>
        <KitContainedIcon name='clock' size='md' />
      </View>
      <View>
        <KitText center h1 style={styles.title} color={Colors.N900}>
          {t('auth:tooManyAttempts')}
        </KitText>
        <KitText center style={styles.subtitle} color={Colors.N900}>
          {t('auth:tooManyAttemptsSubtitle', {
            duration,
            verificationMethod: isEmailVerification
              ? t('auth:verificationMethodEmail')
              : t('auth:verificationMethodPhoneNumber'),
          })}
        </KitText>
      </View>
      <KitButton
        color={Colors.N1000}
        onPress={reset}
        title={t('auth:backToLogin')}
      />
    </View>
  );
};

export default TooManyAttemptsScreen;

const styles = StyleSheet.create({
  contentContainer: {
    paddingHorizontal: Spacing.xl,
    marginBottom: Spacing.xxxxxl,
  },
  iconContainer: {
    marginBottom: Spacing.l,
    alignItems: 'center',
  },
  title: {
    textAlign: 'center',
    marginBottom: Spacing.s,
  },
  subtitle: {
    textAlign: 'center',
    marginBottom: Spacing.xxl,
  },
});
